/**
 * tones.ts — ONE canonical state → BadgeTone map for the staff surfaces.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: P0 (portal foundation).
 *
 * WHY: five admin tabs each hand-rolled their own `Record<State, BadgeTone>`
 * map (callback statuses, assessment states, subscription states, payment
 * phases) — the same colour decisions, duplicated, free to drift. This module
 * is the single authority: `toneFor(state)` answers "what colour is this
 * state chip?" for EVERY staff lifecycle enum, and `labelFor(state)` renders
 * the human label the same way everywhere (underscores → spaces, sentence
 * case). An unknown state falls back to 'neutral' / a best-effort label — a
 * new server enum value must never crash a table.
 *
 * PlanManagement's FORM_CHIP_TONE map deliberately stays local: it keys on
 * the medication delivery-form enum (an R28-sensitive value set), not a
 * lifecycle state — a genuinely different concern.
 *
 * `CHIP_TONES` at the foot is the same authority for the surfaces that cannot
 * use a BadgeTone name: the pipeline's icon chips build their own pill with
 * `chipStyle({ bg, text })`, so they need the token PAIR. It is also where the
 * one hue that is NOT a status lives — see the block above it.
 */
import type { BadgeTone } from './kit';

/** Every staff lifecycle state → its chip tone (the one canonical map). */
export const STATUS_TONES = {
  open: 'pending',
  in_progress: 'neutral',
  escalated: 'pending',
  // CRM pipeline deal stages (2026-07-21) — display names live in
  // src/pipeline/stages.ts STAGE_LABEL; these are the chip tones only.
  mql: 'neutral',
  callback_requested: 'pending',
  // The merged clinical lane (2026-09-05) — one tone where there were two.
  // `eligibility_in_progress` was 'neutral' and `awaiting_clinical_review' was
  // 'pending'; PENDING wins, because the merged lane always means a clinician
  // still owes a decision. Neutral on a queue that is genuinely waiting on
  // somebody would under-state it.
  clinical_review: 'pending',
  awaiting_clinician_followup: 'pending',
  awaiting_admin_followup: 'pending',
  closed_won: 'positive',
  // Renamed from `marketing_funnel` (2026-09-05); MUTED is unchanged and still
  // right — the card is out of the active call rounds, though the patient is
  // very much still a real one we could not reach. Unchanged again on
  // 2026-09-22, when an ADMIN gained a manual move into the lane: who decided
  // nobody could be reached does not change what the lane means.
  unreachable: 'muted',
  closed_lost: 'muted',
  // A merged duplicate is bookkeeping, not an outcome: MUTED, deliberately not
  // the DANGER a double charge wears. The money warning belongs on the money
  // fact (`paymentReviewRequired`), and painting the whole lane red would make
  // the paid duplicates — the only ones anyone has to act on — indistinguishable
  // from the ordinary ones.
  closed_duplicate: 'muted',
  // Auto-set only (3 clinician + 3 admin missed attempts) — muted like closed:
  // no longer an active queue item, though still readable for admin review.
  parked: 'muted',
  closed: 'muted',
  draft: 'muted',
  submitted: 'pending',
  approved: 'positive',
  rejected: 'muted',
  active: 'positive',
  dormant: 'pending',
  past_due: 'pending',
  cancelled: 'muted',
  completed: 'neutral',
  held: 'pending',
  captured: 'positive',
  released: 'muted',
  refunded: 'muted',
  // Fulfilment tail (pharmacy dispensing lane, 2026-07-29). `dispense_rejected`
  // is DANGER, not the muted tone `rejected` wears: a clinician declining a case
  // is a normal clinical outcome, whereas a prescribed order the pharmacy would
  // not dispense is an exception someone has to pick up — the chip should say so
  // at a glance. The two states are kept distinct for exactly that reason.
  awaiting_dispense: 'pending',
  dispensed: 'positive',
  dispatched: 'positive',
  dispense_rejected: 'danger',
  // Assessment ELIGIBILITY verdicts (CTO 2026-08-27). `not_eligible` was
  // missing from this map entirely, so `toneFor` fell through to its
  // 'neutral' default — and 'neutral' is the pale-green/green-text pair, so
  // the board card and the PMS record both painted "NOT ELIGIBLE" GREEN. A
  // negative verdict wearing the positive colour is the one direction that
  // actually misleads: staff scan these chips, and green reads as "cleared".
  // DANGER is the existing token pair (--gps-danger-text #b3261e on
  // --gps-danger-bg #f7dad8), measured at 4.97:1 — past the 4.5:1 bar that
  // applies to the Badge's 11px/700 text, which is below the 18.66px bold
  // "large text" threshold and so does NOT get the 3:1 allowance. NOTE the
  // 6.54:1 recorded next to --gps-danger-text in tokens.css is that ink on
  // WHITE, not on this tint; the headroom on a filled chip is 0.47, not 2.04,
  // so darkening --gps-danger-bg or reusing this pair at a smaller size needs
  // a fresh measurement. It is also the tone the drawer's own ATTEMPT_TONES
  // map already gives this same verdict — the two surfaces had simply drifted.
  //
  // `eligible` is written out even though 'positive' and the old 'neutral'
  // fallback resolve to the SAME tokens: the pair has to be visibly deliberate
  // here, or the next reader cannot tell the green from another omission.
  //
  // `clinician_review` is deliberately NOT mapped. It reaches this fallback
  // badge only on a CLOSED deal (an open one derives the icon-led chip in
  // statusChips.tsx instead), and neither amber "pending" nor green is true of
  // a review on a deal that has already ended — so it keeps the neutral
  // default rather than gaining a colour that asserts something.
  not_eligible: 'danger',
  eligible: 'positive',
} as const;

/** The chip tone for a state; 'neutral' for anything the map doesn't know. */
export function toneFor(state: string): BadgeTone {
  return (STATUS_TONES as Record<string, BadgeTone>)[state] ?? 'neutral';
}

/** Human label for a state: underscores → spaces, sentence case. */
export function labelFor(state: string): string {
  const spaced = state.replace(/_/g, ' ');
  return spaced.length ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}

/* ── CHIP TOKEN PAIRS — the tones a `chipStyle` span needs (2026-09-06) ─────
 *
 * `STATUS_TONES` above answers "what colour is this lifecycle state?" and it
 * answers in BadgeTone NAMES, which is all `ui/kit` Badge accepts. The
 * pipeline's icon chips are not Badges: they are `chipStyle({ bg, text })`
 * spans (pipeline/statusChips.tsx), so they need the token PAIR rather than
 * the name — and until now every one of them carried its own literal, which is
 * precisely the drift this module was created to end. This is that set.
 */

/** A raw chip token pair — the shape `chipStyle` takes. */
export interface ChipTone {
  bg: string;
  text: string;
}

/**
 * ⚠️ `indigo` IS DELIBERATELY NOT A STATUS HUE, AND THAT IS THE WHOLE POINT.
 *
 * The board's colour language is status: green approved, amber waiting, red
 * stopped, grey out of play. PROVENANCE IS NOT A STATUS — "this person filled
 * in the widget form" is not a step in any lifecycle — so an entry-path chip
 * that borrows one of those four asserts a clinical claim it has no business
 * making. The form chip used to wear the pale BRAND GREEN, which was wrong
 * twice over: it read as a verdict, and at 12px it sat one shade from the
 * solid-green callback chip beside it, so the two entry paths — the only
 * surviving record of how a patient arrived, now that cards change lane on
 * approval — were told apart by a tint.
 *
 * ⚠️ WHY A LITERAL HEX AND NOT `var(--gps-*)`. Every other colour in the
 * portal reads a token from theme/tokens.css and that rule is unchanged. There
 * is no indigo in the palette and tokens.css is not this change's to edit; a
 * `var(--gps-indigo)` pointing at nothing resolves to `transparent`, so the
 * chip would silently lose its fill and the white glyph would vanish on the
 * card background. A literal is the honest form of "this hue is not in the
 * design system yet". WHEN IT IS ADDED: move these two values into tokens.css
 * and swap them for the vars — the pair is read in exactly one place.
 *
 * WCAG 2.1 AA, measured: #ffffff on #3949ab is 7.74:1. That clears the 4.5:1
 * bar for the chip's 11px/700 text (well under the 18.66px bold "large text"
 * threshold, so no 3:1 allowance applies) and the 3:1 bar 1.4.11 sets for the
 * glyph as a meaningful graphic. Re-measure before reusing this pair at a
 * lighter weight or over a different ground.
 *
 * `muted` is the pair the Badge 'muted' tone already resolves to, named here so
 * the icon chips that need grey stop re-typing it next to each other.
 */
export const CHIP_TONES: Record<'indigo' | 'muted', ChipTone> = {
  indigo: { bg: '#3949ab', text: '#ffffff' },
  muted: { bg: 'var(--gps-surface-2)', text: 'var(--gps-muted)' },
};
